import { useState } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextField, TextArea, Button, LabelledContainer } from '@hamrolt/components';
import { Section } from './Section';
import { FUNCTIONS_URL } from '../firebase';

type MessageProps = {
	name: string;
	email: string;
	subject: string;
	message: string;
};

interface AlertProps {
	severity: 'success' | 'error';
	message: string;
}
interface FormProps {
	className?: string;
}

const StyledForm = styled.form`
	display: grid;
	grid-gap: ${({ theme }) => theme.spacing.lg};
	grid-template-columns: 1fr 1fr;
	grid-template-areas: 'name email' 'subject subject' 'message message' 'alert alert' 'clear submit';
	width: 600px;
	padding: 16px 80px;
	margin: 0 auto;
`;

const Name = styled(TextField)`
	grid-area: name;

	&:focus {
		outline: green;
	}
`;

const Email = styled(TextField)`
	grid-area: email;
`;

const SubjectLabelledContainer = styled(LabelledContainer)`
	grid-area: subject;
`;

const Message = styled(TextArea)`
	grid-area: message;
`;

const ResetButton = styled(Button)`
	grid-area: clear;
	justify-self: start;
`;

const SubmitButton = styled(Button)`
	grid-area: submit;
	justify-self: end;
`;

const Alert = styled.div<{ $severity: AlertProps['severity']; }>`
	grid-area: alert;
	box-sizing: border-box;
	width: 600px;
	margin: 0 auto;
	padding: 8px 12px;
	color: #fff;
	background-color: ${({ $severity }) => $severity === 'success' ? '#50bea4' : '#F47457'};
	border-radius: 4px;
`;

export const ContactForm = ({ className }: FormProps) => {
	const { t } = useTranslation('contact');
	const [alert, setAlert] = useState<AlertProps | null>(null);
	const { register, reset, handleSubmit } = useForm<MessageProps>();

	const postData = async (data: MessageProps) => {
		try {
			const rawResponse = await fetch(`${FUNCTIONS_URL}/sendMail`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(data)
			});
			const response = await rawResponse.json();
			setAlert({ severity: 'success', message: response.message });
			reset();
		} catch (e) {
			setAlert({ severity: 'error', message: 'Przepraszamy, nastapił problem z połączeniem z serwerem. Spróbuj ponownie pózniej.' });
		}
	};

	return <Section heading="Napisz do mnie" className={className}>
		<StyledForm onSubmit={handleSubmit(postData)}>
			<LabelledContainer startLabel={t('form.name')}>
				<Name
					id="name"
					{...register('name')}
				/>
			</LabelledContainer>
			<LabelledContainer startLabel={t('form.email')}>
				<Email
					id="email"
					{...register('email')}
				/>
			</LabelledContainer>
			<SubjectLabelledContainer startLabel={t('form.subject')}>
				<TextField
					id="subject"
					{...register('subject')}
				/>
			</SubjectLabelledContainer>
			<Message
				rows={10}
				label={t('form.message')}
				{...register('message')}
			/>
			{alert && <Alert $severity={alert.severity}>{alert.message}</Alert>}
			<ResetButton type="reset" variant="outlined">{t('form.reset')}</ResetButton>
			<SubmitButton type="submit" role="primary">{t('form.submit')}</SubmitButton>
		</StyledForm>
	</Section>
}